// src/utils/loadClauseFromV1.js
// Adapter de leitura do schema v1 (src/data/standards/*.json) para a UI de Referência.
// Substitui o uso direto de clauseDetails.json (legado, q/a/s) por leitura
// estruturada cláusula → preamble + items + subitems + annotations + postamble.
//
// Funções:
//   getStandard(normaId) → { meta, clauses } | null
//   loadClause(normaId, clauseId) → { meta, clause } | null
//   findClauseAcrossNorms(clauseId) → [{ normaId, meta, clause }, ...]
//   getActiveStandardIds() → string[] (lê toggle do localStorage)

import { STANDARDS_REGISTRY } from "../modules/standards/registry";

const STANDARDS_FILES = import.meta.glob("../data/standards/*.json", { eager: true });
const TOGGLE_KEY = "norm_standards_toggle";

// Index normaId → standard data (lazy, montado uma vez)
let _index = null;
function getIndex() {
  if (_index) return _index;
  _index = {};
  for (const entry of STANDARDS_REGISTRY) {
    if (!entry.dataFile) continue;
    const filename = entry.dataFile.split("/").pop();
    const key = Object.keys(STANDARDS_FILES).find(k => k.endsWith(`/${filename}`));
    if (!key) continue;
    const data = STANDARDS_FILES[key].default;
    if (!data || !data.meta || !data.clauses) continue;
    _index[entry.id] = data;
  }
  return _index;
}

export function getStandard(normaId) {
  if (!normaId) return null;
  return getIndex()[normaId] || null;
}

// Normaliza chave da cláusula. Aceita "4.1", "4.1 (a)", "4.1 — título", etc.
function normalizeClauseKey(rawId) {
  if (rawId == null) return "";
  return String(rawId)
    .replace(/\s*\(.*\)\s*/g, "")
    .replace(/[—–-]\s.*/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")[0];
}

export function loadClause(normaId, clauseId) {
  const std = getStandard(normaId);
  if (!std) return null;
  const key = normalizeClauseKey(clauseId);
  const clause = std.clauses[key];
  if (!clause) return null;
  return { meta: std.meta, clause };
}

export function findClauseAcrossNorms(clauseId) {
  const key = normalizeClauseKey(clauseId);
  if (!key) return [];
  const idx = getIndex();
  const results = [];
  for (const [normaId, std] of Object.entries(idx)) {
    const clause = std.clauses[key];
    if (clause) results.push({ normaId, meta: std.meta, clause });
  }
  return results;
}

export function getActiveStandardIds() {
  let toggleState = {};
  try {
    const raw = localStorage.getItem(TOGGLE_KEY);
    if (raw) toggleState = JSON.parse(raw);
  } catch (e) {}
  const active = [];
  for (const entry of STANDARDS_REGISTRY) {
    if (entry.status === "locked") { active.push(entry.id); continue; }
    if (entry.status !== "available") continue;
    const toggled = toggleState[entry.id];
    if (toggled === true || (entry.defaultOn && toggled !== false)) active.push(entry.id);
  }
  return active;
}

// Mapping legado q/a/s → normaId v1 (compat com clauseDetails.json e useSearch).
const LEGACY_NORM_KEY_MAP = { q: "iso9001", a: "iso14001", s: "iso45001" };
export function legacyNormKeyToId(legacyKey) {
  return LEGACY_NORM_KEY_MAP[legacyKey] || null;
}

// Lista cláusulas-irmãs (mesmo prefixo, ex: "4.1" → "4.1.1", "4.1.2").
export function getChildClauses(normaId, clauseId) {
  const std = getStandard(normaId);
  if (!std) return [];
  const key = normalizeClauseKey(clauseId);
  const prefix = key + ".";
  return Object.keys(std.clauses)
    .filter(k => k !== key && k.startsWith(prefix))
    .sort((a, b) => {
      const pa = a.split(".").map(Number);
      const pb = b.split(".").map(Number);
      for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
        const da = pa[i] || 0, db = pb[i] || 0;
        if (da !== db) return da - db;
      }
      return 0;
    })
    .map(k => std.clauses[k]);
}
